import { useCallback, useState } from 'react';
import useEftposAccount from '../../../Hooks/UseEftposAccount';
import LoadingPanel from '../../../Abstracts/Components/LoadingPanel';
import useEftposDepositAccounts from '../../../Hooks/UseEftposDepositAccounts';
import useUserApi, { ErrorHandler } from '../../../Services/UserApi';
import Alert from '../Alert';
import { Link } from 'react-router-dom';
import { EftposAccount, EftposDepositAccount } from 'shared-components';
import IconLink from '../../../Abstracts/Components/IconLink';
import styled from 'styled-components';
import { formatBsb } from './EditDepositAccountForm';
import { BackButton } from '../BreadCrumbs';
import LoadingOverlay from '../../../Abstracts/Components/LoadingOverlay';

export default function DepositAccountList() {
	const { eftposAccount, setEftposAccount } = useEftposAccount();
	const [loading, setLoading] = useState(false);
	const eftposDepositAccounts = useEftposDepositAccounts();

	const userApi = useUserApi();
	const setDefaultDepositAccountId = useCallback(
		(depositAccountId: number) => {
			if (!eftposAccount) return;
			const newEftposAccount = {
				...eftposAccount,
				EftposAccountDefaultEftposDepositAccountId: depositAccountId,
			};
			setLoading(true);
			userApi<{ eftposAccount: EftposAccount }>({
				route: 'eftpos/api',
				data: {
					action: 'saveEftposAccount',
					eftposAccount: newEftposAccount,
				},
				errorMessage: 'Error saving default deposit account',
			})
				.then((res) => {
					setEftposAccount(res.data.eftposAccount);
				})
				.catch((e: unknown) => ErrorHandler.toast(e))
				.finally(() => {
					setLoading(false);
				});
		},
		[userApi, eftposAccount, setEftposAccount],
	);

	if (!eftposAccount || !eftposDepositAccounts) {
		return <LoadingPanel />;
	}

	return (
		<>
			<BackButton to="/" />
			<DepositAccountWarning depositAccounts={eftposDepositAccounts} context="deposit-accounts" />
			{eftposDepositAccounts.length !== 0 && (
				<>
					{loading && <LoadingOverlay text={'Saving'} />}
					<Table className="table table-striped">
						<thead>
							<tr>
								<th className="desktop">Default</th>
								<th>Account name</th>
								<th className="desktop">BSB</th>
								<th className="desktop">Account number</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{eftposDepositAccounts.map((depositAccount) => (
								<tr key={depositAccount.EftposDepositAccountId}>
									<td className="desktop is-default">
										<div>
											<input
												type="radio"
												checked={
													eftposAccount.EftposAccountDefaultEftposDepositAccountId ===
													depositAccount.EftposDepositAccountId
												}
												onChange={() =>
													setDefaultDepositAccountId(depositAccount.EftposDepositAccountId)
												}
											/>
										</div>
									</td>
									<td>{depositAccount.EftposDepositAccountName}</td>
									<td className="desktop">
										{formatBsb(depositAccount.EftposDepositAccountBsb ?? '')}
									</td>
									<td className="desktop">{depositAccount.EftposDepositAccountAccount}</td>
									<td className="text-right">
										<IconLink
											to={`/deposit-accounts/edit/${depositAccount.EftposDepositAccountId}`}
											className="btn btn-sm btn-blue edit-btn"
											iconClass="fa fa-edit"
										>
											<span className="desktop">Edit account details</span>
											<span className="mobile">Edit</span>
										</IconLink>
									</td>
								</tr>
							))}
						</tbody>
					</Table>
					<RegisterBtn className="btn btn-blue" to="/deposit-accounts/register" iconClass="fa fa-plus">
						<span>Register a new deposit account</span>
					</RegisterBtn>
				</>
			)}
		</>
	);
}

export const DepositAccountWarning = (props: { depositAccounts?: EftposDepositAccount[]; context: string }) => {
	const { context } = props;
	if (!props.depositAccounts || props.depositAccounts.length !== 0) {
		return null;
	}

	return (
		<Alert className="alert alert-warning">
			<i className="fa fa-exclamation-triangle fa-lg"></i>
			<span>You have no deposit account!</span>
			<Link to={`/deposit-accounts/register?context=${context}`} className="ml-auto btn btn-sm btn-blue">
				Register deposit account
			</Link>
		</Alert>
	);
};

const RegisterBtn = styled(IconLink)`
	align-self: flex-end;
`;

const Table = styled.table`
	.edit-btn {
		.mobile {
			display: none;
		}

		@media (max-width: 768px) {
			.desktop {
				display: none;
			}

			.mobile {
				display: inline;
			}
		}
	}

	th.desktop,
	td.desktop {
		@media (max-width: 768px) {
			display: none;
		}
	}

	.is-default div {
		margin-left: 16px;
	}
`;
